import React, { useEffect, useRef, useState } from "react";
import { toast } from "../ui/sonner";
import { Camera, ChevronLeft, ChevronRight, Images, Loader2, Maximize2, Plus, RotateCcw, Video, X } from "lucide-react";
import { authToken } from "../../lib/api";
import { T } from "../../lib/i18n";
import { uploadChunked, uploadErrText, isCanceled, UPLOAD_MAX } from "../../lib/upload";
import { kindOf } from "../../lib/attach";
import { MediaLightbox } from "../MediaLightbox";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PHOTO_MAX = 60 * 1024 * 1024;
// 视频上限跟切片上传层同源（uploadChunked 的 UPLOAD_MAX = 200MB）——
// 写死另一个数字会出现「前端放行、上传层拒收」的错位。
const VIDEO_MAX = UPLOAD_MAX;
const VIDEO_EXT = /\.(mp4|mov|m4v|webm|avi|3gp|mkv)$/i;
const ACTIVE = new Set(["queued", "preparing", "uploading", "retrying", "offline", "finalizing"]);
const STATE_TXT = {
  queued: ["等待中", "Queued"], preparing: ["压缩处理中…", "Preparing…"], retrying: ["网络波动，自动重试中…", "Network hiccup · retrying…"],
  offline: ["已离线 · 恢复网络后自动继续", "Offline · resumes when back online"], finalizing: ["服务器保存中…", "Saving on server…"], done: ["已上传 ✓", "Uploaded ✓"], canceled: ["已取消", "Canceled"],
};

/* 现场快照：相册 / 拍照 / 选视频 / 拖拽上传 → 照片先缩图、视频原画 → 分片 + 自动重试 + 断点续传；
   逐张进度、失败可单张重试、可取消。视频进轮播但**不参与自动轮播**（正在看时不打扰、切换时跳过）。 */
export function SitePhotosCard({ data, zh, api, id, reload, showUpload, setShowUpload, onArchive }) {
  // ==== 轮播数据源（2026-10-24 老板口径：轮播看「项目当前阶段」的图片）====
  // 后端 /hub 已按当前 phaseIdx 过滤出 stagePhotos；这里再按当前阶段兜底过滤一次，
  // 防止接口降级（stagePhotos 缺失回退全量 photos）时其它阶段的照片混进轮播。
  // 其它阶段的照片仍可从「项目文件 · 按阶段」查看，入口在卡片右上角「文件归档」。
  const CURRENT_MAX = 8; // 引擎阶段 0-7，8=已竣工（与后端 hub_photo_add 的接受范围一致）
  const currentIdx = Math.min(Math.max(Number(data.phaseIdx ?? 0) || 0, 0), CURRENT_MAX);
  const photos = (data.stagePhotos ?? data.photos ?? []).filter((p) => p.phase === currentIdx);
  const [i, setI] = useState(0);
  const manualNavRef = useRef(0);
  // ==== 阶段选择模型（2026-10-24 老板口径）====
  //   · 手动在下拉里选了阶段 → 本次面板会话内所有上传（含拖拽）都挂到所选阶段，而不是当前阶段；
  //   · 没选择（直接拖拽 / 点空态上传）→ 挂「项目当前阶段」；
  //   · 上传面板关闭（手动取消 / 传完自动收起）即清除手动选择，避免上一次选的阶段
  //     悄悄影响之后「没选择」的拖拽。
  // 旧实现把下拉值存成 state 且仅在挂载时初始化一次：项目推进后下拉仍停在旧阶段，
  // 直接拖拽会打上过期阶段标签，与项目文件「按阶段」分组、轮播角标对不上。
  // 现改为「picked(手动选择) ?? 当前阶段」派生值：未手选时实时跟随 data.phaseIdx。
  const [picked, setPicked] = useState(null); // null = 未手动选择
  const phase = picked ?? currentIdx;
  useEffect(() => { if (!showUpload) setPicked(null); }, [showUpload]);
  const [jobs, setJobs] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [imgError, setImgError] = useState("");
  const [imgRetry, setImgRetry] = useState(0);
  const abortRef = useRef(null);
  const fileRef = useRef(null), camRef = useRef(null), vidRef = useRef(null);
  const busy = jobs.some((j) => ACTIVE.has(j.state));
  const photosRef = useRef(photos);
  photosRef.current = photos;
  const isVid = (p) => !!p && (p.kind === "video" || String(p.contentType || "").startsWith("video/"));
  useEffect(() => {
    if (lightbox !== null || photos.length <= 1) return;
    const t = setInterval(() => {
      if (Date.now() - manualNavRef.current <= 8000) return;
      setI((x) => {
        const list = photosRef.current;
        if (!list.length) return x;
        const c = list[Math.min(x, list.length - 1)];
        if (isVid(c)) return x;                              // 正在看视频 → 不自动切走，等用户看完
        for (let k = 1; k <= list.length; k++) {             // 下一项是视频 → 跳过它，继续在照片间轮播
          const n = (x + k) % list.length;
          if (!isVid(list[n])) return n;
        }
        return x;
      });
    }, 3500);
    return () => clearInterval(t);
  }, [photos.length, lightbox]);
  // 离开页面时**不再**中断上传（2026-10-20 用户需求「退出页面也能继续」）。
  //
  // 原实现是 `useEffect(() => () => abortRef.current?.abort(), [])`：一离开这个卡片，
  // 正在传的照片就被掐断。用户切去别的菜单再回来，看到的是"传到一半没了"。
  // 现在的行为：
  //   · 前台预览图那一段照常传完（它决定这张照片能不能立刻看到，且在 IndexedDB 队列之外）；
  //   · 原图补传走全局队列（lib/uploadRunner.js），与组件生命周期完全解耦；
  //   · 真要让用户能主动停的，只剩界面上的「停止」按钮（它显式调 abort）。
  // 代价：离开页面后仍会占用一段网络把预览图传完 —— 这是符合"上传就是上传"预期的。
  // 只有整个页面被关掉 / 刷新才会中断，此时原图任务仍在库里，回来会自动续。
  const url = (f) => `${BACKEND}/api/files/${encodeURIComponent(f.fileId)}/download?auth=${encodeURIComponent(authToken())}`;
  const cur = photos.length ? photos[Math.min(i, photos.length - 1)] : null;
  useEffect(() => { setImgError(""); setImgRetry(0); }, [cur?.fileId]);
  const manualNav = (d) => { manualNavRef.current = Date.now(); setI((x) => (x + d + photos.length) % photos.length); };
  const lightboxItems = photos.map((p) => {
    // 阶段文字与轮播角标同一来源：优先 phaseName（引擎中文名）；
    // 老记录缺 phaseName 时，phase 是数字走引擎阶段表、是字符串（旧7阶段名）直接展示。
    const phaseLabel = p.phaseName
      || (typeof p.phase === "number" ? data.phases?.[p.phase] : p.phase)
      || "";
    return {
      id: p.fileId,
      fileId: p.fileId,
      url: url(p),
      kind: isVid(p) ? "video" : "photo",
      name: p.filename || (isVid(p) ? "site-video" : "site-photo"),
      meta: [phaseLabel, p.uploadedBy].filter(Boolean).join(" · "),
    };
  });

  const upload = async (fileList) => {
    if (busy) return;
    const files = Array.from(fileList || []);
    const picked = [];
    files.forEach((f) => {
      const kind = kindOf(f.name, f.type);
      if (kind === "image") {
        if (f.size > PHOTO_MAX) { toast.error(zh ? `「${f.name}」超过 60MB 上限，已跳过` : `${f.name} exceeds 60MB`); return; }
        picked.push({ f, kind: "image" });
      } else if (kind === "video" || VIDEO_EXT.test(f.name || "")) {
        if (f.size > VIDEO_MAX) { toast.error(zh ? `「${f.name}」超过 ${Math.round(VIDEO_MAX / 1048576)}MB 上限，已跳过` : `${f.name} exceeds ${Math.round(VIDEO_MAX / 1048576)}MB`); return; }
        picked.push({ f, kind: "video" });
      } else {
        toast.error(zh ? `「${f.name}」不是照片或视频文件，已跳过` : `${f.name} is not a photo or video`);
      }
    });
    if (!picked.length) return;
    setShowUpload(true);
    const ctrl = new AbortController(); abortRef.current = ctrl;
    setJobs(picked.map((p, key) => ({ key, name: p.f.name, pct: 0, state: "queued", file: p.f, kind: p.kind })));
    let okImg = 0, okVid = 0;
    for (let k = 0; k < picked.length; k++) {
      const { f, kind } = picked[k];
      const upd = (patch) => setJobs((js) => js.map((j) => (j.key === k ? { ...j, ...patch } : j)));
      try {
        // ⚠️ 这里**不要**自己先 shrinkImage 再传给 uploadChunked（2026-10-20 实测踩坑）。
        // 曾写成「payload = await shrinkImage(f) → uploadChunked(payload, {keepOriginal:true})」，
        // 而 uploadChunked 内部把入参当作「原图」（`const original = file`），压缩后的
        // payload 交给它就等于「原图=压缩图」，`original !== file` 恒为 false，
        // 源图**永远不入队**、下载也拿不到原图 —— 而且界面一切正常，极难发现。
        // 现在直接把用户选的 f 交进去，由 uploadChunked 内部统一压缩：
        //   压缩结果 = 预览图（立刻可用），入参 f = 原图（后台补传）。
        // 状态仍显示「压缩处理中…」——内部压缩期间不会回报进度，正好停在这个状态。
        if (kind === "image") upd({ state: "preparing" });
        if (ctrl.signal.aborted) throw Object.assign(new Error("canceled"), { code: "ERR_CANCELED" });
        // 视频不压（shrink:false），交给服务端 ffmpeg 异步转码 720p
        //
        // keepOriginal（2026-10-20 用户需求「下载时下载源文件」）：现场照片是设计 / 施工的
        // 举证材料，客户日后可能要原图（放大看裂缝、看清门牌号）。所以先传压缩版
        // 让列表立刻刷新，原图在后台补传；用户点下载时拿到的是没动过的那张。
        const fin = await uploadChunked(api, f, {
          signal: ctrl.signal, shrink: kind === "image",
          keepOriginal: kind === "image", onProgress: ({ pct, state }) => upd({ pct, state }),
        });
        try {
          await api.post(`/projects/${id}/hub/photos`, { fileId: fin.fileId, filename: fin.filename || f.name, contentType: fin.contentType || f.type || (kind === "video" ? "video/mp4" : "image/jpeg"), phase: Number(phase) }, { signal: ctrl.signal });
        } catch (e) {
          // The upload may already have been registered before the response was lost.
          if (e?.response?.status !== 409) throw e;
        }
        upd({ state: "done", pct: 100 });
        if (kind === "video") okVid += 1; else okImg += 1;
        reload();
      } catch (e) {
        if (isCanceled(e)) { setJobs((js) => js.map((j) => (ACTIVE.has(j.state) ? { ...j, state: "canceled" } : j))); break; }
        upd({ state: "error", error: uploadErrText(e, zh) });
      }
    }
    abortRef.current = null;
    if (okImg || okVid) {
      const parts = [];
      if (okImg) parts.push(zh ? `${okImg} 张照片` : `${okImg} photo${okImg > 1 ? "s" : ""}`);
      if (okVid) parts.push(zh ? `${okVid} 个视频` : `${okVid} video${okVid > 1 ? "s" : ""}`);
      toast.success(zh ? `已上传 ${parts.join(" · ")} ✓` : `${parts.join(" · ")} uploaded`);
    }
    if (okImg + okVid === picked.length) setTimeout(() => { setJobs([]); setShowUpload(false); }, 900);
  };
  const retryFailed = () => upload(jobs.filter((j) => j.state === "error" || j.state === "canceled").map((j) => j.file));
  const onPick = (e) => { const fs = Array.from(e.target.files || []); e.target.value = ""; if (fs.length) upload(fs); };

  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  useEffect(() => {  // 文件拖到卡片以外时阻止浏览器直接打开图片
    const block = (e) => { if (Array.from(e.dataTransfer?.types || []).includes("Files")) e.preventDefault(); };
    window.addEventListener("dragover", block); window.addEventListener("drop", block);
    return () => { window.removeEventListener("dragover", block); window.removeEventListener("drop", block); };
  }, []);
  const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
  const onDragEnter = (e) => { if (!hasFiles(e)) return; e.preventDefault(); dragDepth.current += 1; setDragging(true); };
  const onDragOver = (e) => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = busy ? "none" : "copy"; };
  const onDragLeave = (e) => { if (!hasFiles(e)) return; e.preventDefault(); dragDepth.current = Math.max(0, dragDepth.current - 1); if (dragDepth.current === 0) setDragging(false); };
  const onDrop = (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault(); dragDepth.current = 0; setDragging(false);
    if (busy) return;
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) upload(files);
  };
  // 拖拽遮罩上的阶段提示：必须展示「这次上传真正会落的阶段」——手选了就用所选阶段，
  // 没手选就是项目当前阶段；与上传后项目文件「按阶段」归组一致。
  const phaseName = phase === CURRENT_MAX ? (zh ? "已竣工" : "Completed") : T((data.phases || [])[Number(phase)] || "");
  // 空态里提示「当前阶段」名：轮播只看当前阶段，空了要讲清楚去哪看其它阶段
  const curStageName = currentIdx === CURRENT_MAX ? (zh ? "已竣工" : "Completed") : T((data.phases || [])[currentIdx] || "");
  return (
    <div className={`relative bg-white rounded-xl border p-4 h-full flex flex-col transition-colors ${dragging ? "border-indigo-400 ring-2 ring-indigo-200" : "border-slate-200"}`} data-testid="hub-photos-card" data-dragging={dragging ? 1 : 0} data-busy={busy ? 1 : 0}
      onDragEnter={onDragEnter} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
      <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={onPick} data-testid="hub-photo-file-input" />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPick} data-testid="hub-photo-camera-input" />
      {/* 视频入口刻意**不带** capture：带上会强制直接开摄像头录像，手机端就选不了相册里已有的视频。
          去掉后手机点它会弹出「录像 / 相册选择」，录和选都能走。 */}
      <input ref={vidRef} type="file" accept="video/*" multiple className="hidden" onChange={onPick} data-testid="hub-photo-video-input" />
      {dragging && (
        <div className="absolute inset-0 z-20 rounded-xl bg-indigo-600/85 backdrop-blur-[2px] flex flex-col items-center justify-center text-white pointer-events-none" data-testid="hub-photo-drop-overlay">
          <Camera className="h-9 w-9 mb-2" />
          <p className="text-sm font-bold">{busy ? (zh ? "上传中，请稍候…" : "Uploading, please wait…") : (zh ? "松开即上传照片 / 视频" : "Drop to upload photos / videos")}</p>
          <p className="text-[11px] opacity-80 mt-1">{zh ? `阶段：${phaseName} · 支持多张` : `Phase: ${phaseName} · multiple`}</p>
        </div>
      )}
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-bold text-slate-800">{zh ? "现场快照" : "Site photos"}
          {/* 轮播只看当前阶段（2026-10-24 口径），头部标注本阶段数量；stagePhotoTotal 是
              该阶段全部记录数（轮播最多只带 8 条），缺失时退回轮播列表长度。 */}
          <span className="ml-1.5 text-[11px] font-medium text-slate-400" data-testid="hub-photos-stage-count">{zh ? `本阶段 ${data.stagePhotoTotal ?? photos.length} 张` : `${data.stagePhotoTotal ?? photos.length} this stage`}</span>
          <button data-testid="hub-photo-upload-btn" onClick={() => setShowUpload(!showUpload)} className="ml-1.5 text-indigo-600 hover:text-indigo-800 align-middle" title={zh ? "上传照片 / 视频" : "Upload"}><Plus className="h-4 w-4 inline" /></button>
        </p>
        <button data-testid="hub-photos-archive" onClick={onArchive} className="text-[11px] font-semibold text-indigo-600 hover:underline">{zh ? `文件归档 · 现场记录 ${data.photoTotal || 0}` : `Archive · ${data.photoTotal || 0}`}</button>
      </div>
      {showUpload && (
        <div className="mb-2 rounded-lg bg-slate-50 p-2 space-y-1.5" data-testid="hub-photo-upload-panel">
          <div className="flex gap-1.5 items-center flex-wrap">
            <select value={phase} onChange={(e) => setPicked(Number(e.target.value))} disabled={busy} className="text-[11px] rounded border border-slate-200 px-1.5 py-1 bg-white" data-testid="hub-photo-phase-select">
              {(data.phases || []).map((n, x) => <option key={x} value={x}>{T(n)}</option>)}
              {/* 引擎阶段 8 = 已竣工：已竣工项目（phaseIdx=8）的默认值落在这里，也可主动补传竣工后照片 */}
              <option value={CURRENT_MAX}>{zh ? "已竣工" : "Completed"}</option>
            </select>
            <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="hub-photo-dropzone"
              className={`flex-1 min-w-[120px] text-center text-[11px] font-semibold rounded border border-dashed px-2 py-1 inline-flex items-center justify-center gap-1 ${busy ? "text-slate-300 border-slate-200" : "text-indigo-600 border-indigo-300 hover:bg-indigo-50"}`}>
              <Images className="h-3 w-3" />{busy ? (zh ? "上传中…" : "Uploading…") : (zh ? "照片 / 视频 · 相册或拖拽" : "Photos / videos · pick or drop")}
            </button>
            <button type="button" disabled={busy} onClick={() => camRef.current?.click()} data-testid="hub-photo-camera-label"
              className={`text-center text-[11px] font-semibold rounded px-2.5 py-1 inline-flex items-center gap-1 ${busy ? "text-slate-300 bg-slate-100" : "text-white bg-indigo-600 hover:bg-indigo-700"}`}>
              <Camera className="h-3 w-3" />{zh ? "拍照" : "Camera"}
            </button>
            <button type="button" disabled={busy} onClick={() => vidRef.current?.click()} data-testid="hub-photo-video-label"
              className={`text-center text-[11px] font-semibold rounded px-2.5 py-1 inline-flex items-center gap-1 ${busy ? "text-slate-300 bg-slate-100" : "text-white bg-indigo-600 hover:bg-indigo-700"}`}>
              <Video className="h-3 w-3" />{zh ? "选视频" : "Videos"}
            </button>
            {busy
              ? <button type="button" onClick={() => abortRef.current?.abort()} className="text-red-500 text-[11px] font-semibold inline-flex items-center gap-0.5" data-testid="hub-photo-cancel-all"><X className="h-3 w-3" />{zh ? "停止" : "Stop"}</button>
              : <button type="button" onClick={() => { setJobs([]); setShowUpload(false); }} className="text-slate-400 text-[11px]" data-testid="hub-photo-upload-cancel">{zh ? "取消" : "Cancel"}</button>}
          </div>
          {jobs.length > 0 && (
            <ul className="space-y-1" data-testid="hub-photo-jobs">
              {jobs.map((j) => (
                <li key={j.key} className="text-[11px]" data-testid={`hub-photo-job-${j.key}`} data-state={j.state} data-kind={j.kind}>
                  <div className="flex items-center gap-1.5">
                    {ACTIVE.has(j.state) ? <Loader2 className="h-3 w-3 animate-spin text-indigo-500 shrink-0" /> : <span className={`h-2 w-2 rounded-full shrink-0 ${j.state === "done" ? "bg-emerald-500" : "bg-red-400"}`} />}
                    {j.kind === "video" && <Video className="h-3 w-3 text-slate-400 shrink-0" />}
                    <span className="truncate text-slate-700 flex-1">{j.name}</span>
                    <span className={`shrink-0 ${j.state === "error" ? "text-red-600" : j.state === "done" ? "text-emerald-600" : "text-slate-500"}`}>
                      {j.state === "uploading" ? `${j.pct}%` : j.state === "error" ? j.error : (STATE_TXT[j.state] || [j.state, j.state])[zh ? 0 : 1]}
                    </span>
                  </div>
                  {ACTIVE.has(j.state) && <div className="h-1 rounded-full bg-slate-200 mt-0.5 overflow-hidden"><div className="h-full bg-indigo-500 transition-[width]" style={{ width: `${j.pct}%` }} /></div>}
                </li>
              ))}
              {!busy && jobs.some((j) => j.state === "error" || j.state === "canceled") && (
                <li><button type="button" onClick={retryFailed} className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-600 hover:underline" data-testid="hub-photo-job-retry"><RotateCcw className="h-3 w-3" />{zh ? "重试失败的项" : "Retry failed"}</button></li>
              )}
            </ul>
          )}
          <p className="text-[10px] text-slate-400">{zh ? "照片自动压缩、视频保留原画，均分片上传（大文件断点续传、断网自动继续）；归入左侧所选阶段（未选则按项目当前阶段），项目文件按阶段归组 · 也可直接把文件拖到本卡片任意位置（未选阶段=当前阶段）" : "Photos are shrunk, videos kept original — both uploaded in chunks with auto-retry/resume · filed under the selected phase (or the current one if untouched) · drop anywhere on this card"}</p>
        </div>
      )}
      {!cur ? (
        <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="hub-photo-empty-pick"
          className="relative flex-1 min-h-[180px] rounded-lg overflow-hidden bg-slate-900 flex flex-col items-center justify-center text-center transition-colors disabled:opacity-60">
          {/* 老板口径（2026-10-24）：当前阶段没有照片，就在轮播图正中间显示这句话。
              容器用与轮播一致的深色底，避免有无照片时卡片观感跳变；整块仍可点击选图，
              拖拽上传照常作用于整卡。 */}
          {busy ? <Loader2 className="h-8 w-8 mb-2 animate-spin text-indigo-400" /> : <Camera className="h-7 w-7 mb-2 text-slate-500" />}
          <p className="text-sm font-semibold text-slate-200 px-4" data-testid="hub-photo-empty-text">{zh ? `「${curStageName}」阶段没有照片` : `No photos in "${curStageName}" yet`}</p>
          <p className="text-[11px] text-slate-400 mt-1.5 px-6">{zh ? "点击拍照 / 选图，或直接拖到卡片上（上传挂当前阶段）· 其它阶段见「项目文件 · 按阶段」" : "Tap to pick or drop files here (uploads go to the current phase) · other phases: Project Files → By phase"}</p>
        </button>
      ) : (
        <div className="relative flex-1 min-h-[180px] rounded-lg overflow-hidden bg-slate-900">
          {isVid(cur)
            ? <video key={cur.fileId} src={url(cur)} controls playsInline preload="metadata" className="absolute inset-0 w-full h-full object-contain" data-testid="hub-photo-video" />
            : (
              <button type="button" onClick={() => setLightbox(i)} className="absolute inset-0 w-full h-full group focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
                aria-label={zh ? `查看现场照片 ${i + 1}` : `View site photo ${i + 1}`} data-testid="hub-photo-preview">
                {/* 大图完整显示（2026-10-24 老板需求「根据 box 宽度让图片展示全」）：
                    原先 object-cover 裁切填满卡片，竖图/横图都会被裁掉一大截；
                    改为 object-contain 完整显示，卡片深色底（bg-slate-900）补边。 */}
                <img key={`${cur.fileId}-${imgRetry}`} src={url(cur)} alt={cur.filename || "site"} onError={() => setImgError("failed")}
                  className="absolute inset-0 w-full h-full object-contain transition-transform duration-200 group-hover:scale-[1.02]" data-testid="hub-photo-img" />
                <span className="absolute top-2 right-2 inline-flex items-center gap-1 rounded bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                  <Maximize2 className="h-2.5 w-2.5" />{zh ? "查看大图" : "View"}
                </span>
              </button>
            )}
          {!isVid(cur) && imgError && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-slate-950/85 px-4 text-center text-white" data-testid="hub-photo-load-error">
              <p className="text-xs font-semibold">{zh ? "照片加载失败" : "Photo failed to load"}</p>
              <p className="max-w-xs text-[10px] text-slate-300">{zh ? "可能是网络波动或登录态刚恢复，重试不会改动原文件。" : "This may be a network or session hiccup. Retrying does not change the file."}</p>
              <button type="button" onClick={() => { setImgError(""); setImgRetry((v) => v + 1); }} data-testid="hub-photo-load-retry"
                className="inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-800 hover:bg-slate-100">
                <RotateCcw className="h-3 w-3" />{zh ? "重新加载" : "Retry"}
              </button>
            </div>
          )}
          {/* 角标=这张照片落库时记录的阶段（与项目文件「按阶段」分组同源，2026-10-24 口径）：
              新记录有 phaseName（引擎中文名）；老的视频记录缺 phaseName 且 phase 可能是旧7阶段名
              字符串，数字走引擎阶段表、字符串直接展示，保证角标文字与归组文字对得上。 */}
          {cur.phase != null && <span className="absolute top-2 left-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-black/55 text-white" data-testid="hub-photo-phase-tag">{T(cur.phaseName) || (typeof cur.phase === "number" ? T((data.phases || [])[cur.phase] || "") : T(cur.phase))}</span>}
          {isVid(cur) && <span className="absolute top-2 right-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-600/85 text-white inline-flex items-center gap-0.5" data-testid="hub-photo-video-tag"><Video className="h-2.5 w-2.5" />{zh ? "视频" : "Video"}</span>}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
            <p className="text-[11px] text-white truncate">{cur.filename}{cur.uploadedBy ? ` · ${cur.uploadedBy}` : ""}</p>
          </div>
          {photos.length > 1 && (
            <>
              <button data-testid="hub-photo-prev" onClick={() => manualNav(-1)} title={zh ? "上一个" : "Prev"}
                className="absolute left-1.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center transition-colors">
                <ChevronLeft className="h-5 w-5" /></button>
              <button data-testid="hub-photo-next" onClick={() => manualNav(1)} title={zh ? "下一个" : "Next"}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center transition-colors">
                <ChevronRight className="h-5 w-5" /></button>
            </>
          )}
          {photos.length > 1 && (
            <div className="absolute bottom-1.5 right-2 flex gap-1">
              {photos.map((p, x) => <button key={x} onClick={() => manualNav(x - i)} className={`h-1.5 rounded-full transition-all ${x === i ? "w-4 bg-white" : "w-1.5 bg-white/50"}`} title={isVid(p) ? (zh ? "视频" : "Video") : ""} />)}
            </div>
          )}
        </div>
      )}
      {lightbox !== null && lightboxItems[lightbox] && (
        <MediaLightbox
          tid="hub-photo-lightbox"
          items={lightboxItems}
          index={lightbox}
          onIndex={(next) => { manualNavRef.current = Date.now(); setLightbox(next); setI(next); }}
          onClose={() => setLightbox(null)}
          api={api}
          zh={zh}
        />
      )}
    </div>
  );
}
